/**
 * @bos/portal3 — THE OVERVIEW (`/app/runs/:id`): the front desk of a build.
 *
 * The page a returning owner actually needs, in the order they need it:
 *
 *   1. THE ONE THING THAT NEEDS YOU — a door to open, a round of questions to answer
 *      (rendered right here, not hunted for at the bottom of a log), or the plain truth
 *      that nothing needs them and what the machine is doing instead.
 *   2. TAKE YOUR APP — on a finished build, immediately: it is what they came for.
 *   3. YOUR BRIEF, AS UNDERSTOOD — the claims the machine grounded this build on, when
 *      the run carries them. A run from before the intake leg carries none, and this
 *      section is simply absent: an empty "no claims" card would be a worse lie than
 *      silence, and both of today's builds pre-date it.
 *   4. THE MILESTONES — the clean story, one row per stage the run really reached, each
 *      opening its room. Not the 7,201-item thread; that is the Journal's job now.
 *   5. SETTLED FOR YOU — the assumptions the machine made on the owner's behalf, with
 *      their correction doors, and any answers still on record.
 *   6. CHANGES YOU ORDERED — the owner-order ledger and its withdraw door.
 *   7. WHAT THIS BUILD READ — the documents record, frozen at launch.
 *
 * WHAT IS DELIBERATELY NOT HERE: cost. The frames carry it; the owner deferred showing
 * it; nothing on this page draws a number the server did not send.
 */
import { Link } from 'react-router-dom';
import { PATHS } from '../../routes';
import { useOwnerEdits, useRunAppPackage, useRunDocuments } from '../hooks';
import { AdvisoriesBlock, AdvisoriesRouted, OrphanedAnswers, QuestionList, QuestionRound } from '../QuestionRound';
import { SubjectStrip } from '../SubjectStrip';
import { AppDownload } from '../AppDownload';
import { OwnerEdits } from '../OwnerEdits';
import { RunDocuments } from '../RunDocuments';
import { useRunRoom } from '../roomContext';
import { countPhrase, routeQuestions } from '../questionRouting';
import {
  deriveMilestones,
  formatClock,
  isReplayNote,
  statusSentence,
  stallKindOf,
  workingSentence,
} from '../model';

export function OverviewRoom() {
  const {
    runId,
    view,
    ready,
    statusKey,
    terminal,
    ownerCall,
    actionBusy,
    onResume,
    questions,
    qStatus,
    submitAnswers: submit,
    intake,
  } = useRunRoom();

  const appPackage = useRunAppPackage(runId, ready, statusKey);
  const ownerEditsState = useOwnerEdits(runId, ready, statusKey);
  const runDocuments = useRunDocuments(runId, ready);

  const milestones = deriveMilestones(view);
  /** The round's overview-routed remainder: questions whose stage names no room. They are
   *  asked HERE even at a stage-door halt — nowhere else can hold them (the never-stranded
   *  law questionRouting promises). */
  const overviewQuestions =
    qStatus === 'ready' && questions != null ? routeQuestions(questions.round.questions).overview : [];

  // The front desk holds only what no room can: a question whose stage names no room at all.
  // Its strip has no room to draw a kind→claims fallback from, so it quotes the brief or says
  // nothing — the same rule as everywhere else.
  const stripFor = (q: Parameters<typeof SubjectStrip>[0]['q']) => (
    <SubjectStrip q={q} intake={intake} room={null} artifactDerived={false} />
  );

  return (
    <div className="ovw">
      {/* 1 ── the one thing that needs you ─────────────────────────────────── */}
      {ownerCall === null ? (
        <section className="needs needs--calm" aria-label="What needs you">
          <h2 className="needs__title">
            {terminal && view.status !== 'completed' ? 'This build isn’t running' : 'Nothing needs you'}
          </h2>
          <p className="needs__body">
            {terminal
              ? statusSentence(view.status, stallKindOf(view))
              : `Right now ${workingSentence(view)}. You can close this page — the run keeps going, and this page rebuilds itself when you come back.`}
          </p>
        </section>
      ) : ownerCall.kind === 'stage-door' ? (
        <section className="needs needs--door" aria-label="What needs you">
          <h2 className="needs__title">
            {ownerCall.stage === 'er'
              ? 'Your data model is ready for you'
              : `The ${ownerCall.label} review is ready for you`}
          </h2>
          <p className="needs__body">
            Read it and approve it to continue. Nothing more is spent while it waits — and
            if it isn’t right, the same screen is where you say so.
          </p>
          <Link className="btn btn--primary" to={PATHS.runRoom(runId, ownerCall.room)}>
            {ownerCall.stage === 'er' ? 'Open your data model' : `Open ${ownerCall.label}`}
          </Link>
        </section>
      ) : (
        <section className="needs needs--ask" aria-label="What needs you">
          {qStatus === 'loading' && (
            <p className="page__note">Fetching what the machine wants to ask…</p>
          )}
          {qStatus === 'ready' && questions !== null && (
            <QuestionRound
              runId={runId}
              questions={questions}
              onSubmit={submit}
              onResume={onResume}
              resumeBusy={actionBusy}
              stripFor={stripFor}
            />
          )}
          {qStatus === 'none' && (
            <div className="qround__resume">
              <h2 className="needs__title">The machine is waiting</h2>
              <p className="page__note">
                It stopped, and no question round was published for this stop. If a fix has
                landed since, resume the build — finished stages replay free.
              </p>
              <button
                className="btn btn--primary btn--sm"
                type="button"
                onClick={onResume}
                disabled={actionBusy}
              >
                {actionBusy ? 'Resuming…' : 'Resume the build'}
              </button>
            </div>
          )}
        </section>
      )}

      {/* 2 ── take your app: on a finished build it is the thing itself ────── */}
      {terminal && (
        <AppDownload
          runId={runId}
          payload={appPackage.appPackage}
          status={appPackage.apStatus === 'idle' ? 'loading' : appPackage.apStatus}
          error={appPackage.apError}
          terminal={terminal}
        />
      )}

      {/* 3 ── your brief, as understood ────────────────────────────────────── */}
      {intake !== undefined && (
        <section className="ovw-brief" aria-label="Your brief, as understood">
          <h2 className="ovw__title">Your brief, as understood</h2>
          {intake.status === 'failed' || intake.claims.length === 0 ? (
            <p className="page__note">
              Your brief could not be grounded
              {intake.status === 'failed' && intake.reason !== undefined
                ? ` — ${intake.reason}`
                : ''}
              . This build was made from the raw brief instead, and its final report says
              the spec-fidelity check could not compute.
            </p>
          ) : (
            <>
              <p className="ovw__lede">
                The machine read your brief and pulled {intake.claims.length} claim
                {intake.claims.length === 1 ? '' : 's'} out of it, word for word. Your data
                model was derived from them, and the finished app is checked against them.
                {intake.openDecisions.length > 0 &&
                  ` ${intake.openDecisions.length} ${intake.openDecisions.length === 1 ? 'decision was' : 'decisions were'} left open with a default.`}
              </p>
              <Link className="btn btn--quiet btn--sm" to={PATHS.runRoom(runId, 'plan/data-model')}>
                Read the claims
              </Link>
            </>
          )}
        </section>
      )}

      {/* 4 ── the milestones ───────────────────────────────────────────────── */}
      {milestones.length > 0 && (
        <section className="ovw-mstones" aria-label="The milestones">
          <h2 className="ovw__title">The milestones</h2>
          <p className="ovw__lede">
            What this build has actually been through. Stages the machine replayed from a
            checkpoint without new work are not events, so they are not here — the Journal
            keeps every line.
          </p>
          <ol className="mstones">
            {milestones.map((m) => (
              <li key={m.key} className={`mstone mstone--${m.tone}`}>
                <span className="mstone__when">{m.at !== null ? formatClock(m.at) : ''}</span>
                <span className="mstone__what">
                  {m.room !== undefined ? (
                    <Link className="mstone__link" to={PATHS.runRoom(runId, m.room)}>
                      {m.label}
                    </Link>
                  ) : (
                    <span className="mstone__label">{m.label}</span>
                  )}
                  {m.note !== undefined && !isReplayNote(m.note) && (
                    <span className="mstone__note">{m.note}</span>
                  )}
                  {m.detail !== undefined && <span className="mstone__detail">{m.detail}</span>}
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* 5 ── settled for you: the standing advisories + banked answers ──────
             Each advisory carries the stage that settled it, so it lives in that stage's
             room now — the front desk keeps the count and the way there. What has no stage
             (a banked answer whose question drifted away) stays here, because there is no
             room it could honestly be filed under.

             RENDERED WHENEVER SECTION 1 IS NOT ALREADY RENDERING THE ROUND: none of this is
             door-specific, and gating it on "not awaiting the owner" dropped it exactly at a
             stage-door halt — an open round's overview-routed questions, the overview-routed
             advisories and the orphaned answers rendered NOWHERE, breaking the routing's own
             never-stranded guarantee. When section 1 renders the full QuestionRound (the
             'questions'/'unnamed' call), THAT already carries all of this — rendering it here
             too would double it. */}
      {qStatus === 'ready' && questions != null && (ownerCall === null || ownerCall.kind === 'stage-door') && (
        <>
          {ownerCall !== null && overviewQuestions.length > 0 && (
            <section className="needs needs--ask" aria-label="Also waiting for you">
              <p className="qround__here">
                {countPhrase(overviewQuestions.length)} from this stop{' '}
                {overviewQuestions.length === 1 ? 'belongs' : 'belong'} to no one part of your app, so{' '}
                {overviewQuestions.length === 1 ? 'it is' : 'they are'} asked right here.
              </p>
              <QuestionList
                questions={overviewQuestions}
                answers={questions.answers}
                onSubmit={submit}
                stripFor={stripFor}
              />
            </section>
          )}
          <AdvisoriesRouted runId={runId} advisories={questions.round.advisories ?? []} />
          <AdvisoriesBlock
            advisories={routeQuestions(questions.round.advisories ?? []).overview}
            answers={questions.answers}
            onSubmit={submit}
            runClosed={view.status === 'completed'}
            intro="The machine settled these on your behalf during this build, and they belong to no one part of your app. If one is wrong, correct it — this build re-derives with your words when it next runs."
            stripFor={stripFor}
          />
          <OrphanedAnswers round={questions.round} answers={questions.answers} />
        </>
      )}

      {/* 6 ── changes you ordered ──────────────────────────────────────────── */}
      <OwnerEdits
        payload={ownerEditsState.ownerEdits}
        status={ownerEditsState.oeStatus}
        completed={view.status === 'completed'}
        onWithdraw={ownerEditsState.withdraw}
      />

      {/* 7 ── what this build read ─────────────────────────────────────────── */}
      <RunDocuments
        payload={runDocuments.documents}
        status={runDocuments.dStatus === 'idle' ? 'loading' : runDocuments.dStatus}
        error={runDocuments.dError}
      />
    </div>
  );
}
